import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { DatePicker, Card, Typography, Alert, Select, Button, Space } from 'antd';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

// Import services
import { getSoilByExploitationAndPointNumber } from '../../../Utils/informations/soil';
import { getPointsByExploitation, getPointAudits, getPointInfo } from '../../../Utils/exploitations/points';
import { getProductByExploitation } from '../../../Utils/products/products';
import { getValveInfo } from '../../../Utils/exploitations/valves';

// Import icons
import tempIcon from '../../../assets/icons/Temp.svg';
import humidityIcon from '../../../assets/icons/Humd.svg';
import pHIcon from '../../../assets/icons/pH.svg';
import elecCondIcon from '../../../assets/icons/Econd.svg';
import potassiumIcon from '../../../assets/icons/potassium.svg';
import phosphorusIcon from '../../../assets/icons/phosphrus.svg';
import nitrogenIcon from '../../../assets/icons/nitrogen.svg';
// Add battery and sensor temp icons - you may need to import these
import batteryIcon from '../../../assets/icons/battery.png';
import sensorTempIcon from '../../../assets/icons/boardTemp.png';

// Import components
import Loading from '../../Shared/Loading';
import SoilMetricsGraphs from './Graphs';
import DateRangeHandler from './DateRangeHandler';
import RenderSensorCard from './SensorCard';

dayjs.extend(isBetween);

const { Title, Text } = Typography;

const USER_ROLE_ROUTES = {
  client: '/client',
  admin: '/admin',
  superadmin: '/admin',
  domain_manager: '/domain-manager',
  exploitation_manager: '/exploitation-manager'
};

const INITIAL_DATE_RANGE = [dayjs().subtract(7, 'days'), dayjs()];

const SoilInfoPage = () => {
    const { exploitationId } = useParams();
    const navigate = useNavigate();
    const userRole = useSelector((state) => state.user.userRole.name);
    
    // State management
    const [state, setState] = useState({
        soilData: [],
        exploitationPoints: [],
        selectedPoint: null,
        loading: false,
        showGraphs: false,
        supportedSensors: { lvl1: [], lvl2: [] },
        error: null,
        dateRange: INITIAL_DATE_RANGE,
        pointAudits: [],
        dateRangeOptions: [],
        selectedDateRange: null,
        pointInfo: null,
        valveInfo: null
    });

    // Memoized values
    const userLink = useMemo(() => USER_ROLE_ROUTES[userRole] || '/', [userRole]);
    
    // Check if user is admin or superadmin
    const isAdminUser = useMemo(() => 
        userRole === 'admin' || userRole === 'superadmin', 
        [userRole]
    );

    // Handlers
    const handleError = useCallback((error, customMessage) => {
        console.error(customMessage, error);
        setState(prev => ({
            ...prev,
            error: error.message || customMessage,
            loading: false
        }));
    }, []);

    const fetchSoilData = useCallback(async (pointNumber, dateRange) => {
        if (!dateRange) return;
        
        setState(prev => ({ ...prev, loading: true, error: null }));
        try {
            const [start, end] = dateRange.split(',').map(date => 
                dayjs(date).format('YYYY-MM-DD')
            );
            
            const data = await getSoilByExploitationAndPointNumber(
                exploitationId,
                pointNumber,
                start,
                end
            );
            
            if (!data?.length) {
                throw new Error("No soil data found for this exploitation and point.");
            }
            
            setState(prev => ({ 
                ...prev, 
                soilData: data,
                loading: false 
            }));
        } catch (error) {
            handleError(error, "Error fetching soil data");
        }
    }, [exploitationId, handleError]);

    // fetch valve info
    const fetchValveInfo = useCallback(async (valveId) => {
        try {
            const info = await getValveInfo(valveId);
            setState(prev => ({ ...prev, valveInfo: info.data }));
        } catch (error) {
            handleError(error, "Error fetching valve info");
        }
    }, [exploitationId, handleError]);

    // fetch point audits
    const fetchPointAudits = useCallback(async (pointId) => {
        try {
            const audits = await getPointAudits(pointId);
            if (audits.length === 0) {
                // set data range to the full date range
                setState(prev => ({ ...prev, pointAudits: audits, dateRangeOptions: [] }));
                return;
            }
            const dateRangeOptions = audits.map(audit => ({
                label: `${audit.start_date} - ${audit.end_date}`,
                value: `${audit.start_date},${audit.end_date}`
            }));

            setState(prev => ({ ...prev, pointAudits: audits, dateRangeOptions }));
        } catch (error) {
            handleError(error, "Error fetching point audits");
        }
    }, [exploitationId, handleError]);

    // fetch point info
    const fetchPointInfo = useCallback(async (pointId) => {
        try {
            const info = await getPointInfo(pointId);
            setState(prev => ({ ...prev, pointInfo: info.data }));
            if (info.data?.valve_id) {
                fetchValveInfo(info.data.valve_id);
            }
        } catch (error) {
            handleError(error, "Error fetching point info");
        }
    }, [exploitationId, handleError]);

    // Effect to fetch point audits
    useEffect(() => {
        if (!state.selectedPoint) return;
        fetchPointAudits(state.selectedPoint?.point_id);
    }, [state.selectedPoint, fetchPointAudits]);

    // Effect to fetch point info
    useEffect(() => {
        if (!state.selectedPoint) return;
        fetchPointInfo(state.selectedPoint?.point_id);
    }, [state.selectedPoint, fetchPointInfo]);

    // Effect to load initial data
    useEffect(() => {
        const fetchInitialData = async () => {
            setState(prev => ({ ...prev, loading: true, error: null }));
            try {
                const [points, product] = await Promise.all([
                    getPointsByExploitation(exploitationId),
                    getProductByExploitation(exploitationId)
                ]);

                if (!points.data?.length) {
                    throw new Error("No points found for this exploitation.");
                }

                if (!product.data) {
                    throw new Error("No product found for this exploitation.");
                }

                const supportedSensors = {
                    lvl1: product.data.level1_type_sensors?.split(',') || [],
                    lvl2: product.data.level2_type_sensors?.split(',') || []
                };

                setState(prev => ({
                    ...prev,
                    exploitationPoints: points.data,
                    selectedPoint: points.data[0],
                    supportedSensors,
                    loading: false
                }));

                // Fetch initial soil data
                await fetchSoilData(
                    points.data[0].point_number,
                    `${INITIAL_DATE_RANGE[0].format('YYYY-MM-DD')},${INITIAL_DATE_RANGE[1].format('YYYY-MM-DD')}`
                );
            } catch (error) {
                handleError(error, "Error loading initial data");
            }
        };

        fetchInitialData();
    }, [exploitationId, fetchSoilData, handleError]);

    // Render methods
    const RenderSensorCards = () => {
        const latestData = state.soilData[state.soilData.length - 1];
        if (!latestData) return null;

        const sensorConfigs = [
            { icon: tempIcon, title: 'Temperature', value: [latestData.temp_1, latestData.temp_2], unit: '°C', sensorType: 'Temp' },
            { icon: humidityIcon, title: 'Humidity', value: [latestData.hum_1, latestData.hum_2], unit: '%', sensorType: 'Hum' },
            { icon: pHIcon, title: 'pH', value: latestData.ph, unit: '', sensorType: 'pH' },
            { icon: elecCondIcon, title: 'EC', value: [latestData.ec_1, latestData.ec_2], unit: 'µS/cm', sensorType: 'EC' },
            { icon: nitrogenIcon, title: 'Nitrogen (N)', value: latestData.N, unit: 'mg/kg', sensorType: 'N' },
            { icon: phosphorusIcon, title: 'Phosphorus (P)', value: latestData.P, unit: 'mg/kg', sensorType: 'P' },
            { icon: potassiumIcon, title: 'Potassium (K)', value: latestData.K, unit: 'mg/kg', sensorType: 'K' }
        ];

        // Add admin-only sensor cards
        if (isAdminUser) {
            sensorConfigs.push(
                { 
                    icon: batteryIcon, 
                    title: 'Battery Voltage', 
                    value: latestData.battery_level || latestData.battery_lvl, 
                    unit: 'V', 
                    sensorType: 'Battery' 
                },
                { 
                    icon: sensorTempIcon, 
                    title: 'Sensor Temperature', 
                    value: latestData.sensor_temp || latestData.sensor_temperature, 
                    unit: '°C', 
                    sensorType: 'SensorTemp' 
                }
            );
        }


        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {sensorConfigs.map((config, index) => (
                    <RenderSensorCard
                        key={index}
                        {...config}
                        supportedSensors={state.supportedSensors}
                        isAdminUser={isAdminUser}

                    />
                ))}
            </div>
        );
    };

    if (state.loading) return <Loading />;

    return (
        <div className="container mx-auto p-4">
            <div className="flex justify-between items-center mb-8">
                <Title level={2} className="text-indigo-600">
                    Soil Information
                    {isAdminUser && (
                        <span className="text-sm text-gray-500 ml-2">
                            (Admin View - Extended Data)
                        </span>
                    )}
                </Title>
                <Button
                    type="primary"
                    className="bg-blue-500 hover:bg-blue-600"
                    onClick={() => navigate(`${userLink}/exploitation/${exploitationId}/info/irrigation`)}
                >
                    View Irrigation Information
                </Button>
            </div>

            <Card className="mb-8 shadow-md">
                <Space direction="vertical" size="middle" className="w-full">
                    <div className="flex flex-col md:flex-row gap-8 items-center justify-around">
                        <div className="w-full md:w-auto">
                            <Text strong className="mr-2">Select Point:</Text>
                            <select
                                onChange={(e) => {
                                    const point = state.exploitationPoints.find(
                                        p => p.point_number === Number(e.target.value)
                                    );
                                    setState(prev => ({ ...prev, selectedPoint: point }));
                                    if (state.selectedDateRange) {
                                        fetchSoilData(point.point_number, state.selectedDateRange);
                                    }
                                }}
                                value={state.selectedPoint?.point_number}
                                className="p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                {state.exploitationPoints.map((point) => (
                                    <option key={point.point_id} value={point.point_number}>
                                        Point {point.point_number}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {state.selectedPoint?.valve_id ? (
                            <div>
                                <Text strong>La vanne associée actuellement:</Text>
                                <Text className="ml-2 font-bold text-indigo-600">
                                    {state.valveInfo?.valve_name}
                                </Text>
                            </div>
                        ) : state.selectedPoint?.valve_name ? (
                            <div>
                                <Text strong>La vanne associée actuellement:</Text>
                                <Text className="ml-2 font-bold text-indigo-600">
                                    {state.selectedPoint.valve_name}
                                </Text>
                            </div>
                        ) : (
                            <div>
                                <Text strong>La vanne associée actuellement:</Text>
                                <Text className="ml-2 font-bold text-red-600">
                                    Aucune vanne associée
                                </Text>
                            </div>
                        )}

                        {/* a refresh button to refresh the data */}
                        <Button
                            type="primary"
                            className="bg-indigo-600 hover:bg-indigo-700"
                            onClick={() => {
                                if (state.selectedPoint) {
                                    // If there's a selectedDateRange, use that
                                    if (state.selectedDateRange) {
                                        fetchSoilData(
                                            state.selectedPoint.point_number,
                                            state.selectedDateRange
                                        );
                                    }
                                    // Otherwise format and use the dateRange
                                    else {
                                        fetchSoilData(
                                            state.selectedPoint.point_number,
                                            `${state.dateRange[0].format('YYYY-MM-DD')},${state.dateRange[1].format('YYYY-MM-DD')}`
                                        );
                                    }
                                }
                            }}
                        >
                            Refresh Data
                        </Button>
                    </div>

                    <DateRangeHandler 
                        dateRange={state.dateRange}
                        onDateRangeChange={(dates) => {
                            setState(prev => ({ ...prev, dateRange: dates }));
                            if (state.selectedPoint) {
                                fetchSoilData(state.selectedPoint.point_number, `${dates[0].format('YYYY-MM-DD')},${dates[1].format('YYYY-MM-DD')}`);
                            }
                        }}
                        pointAudits={state.pointAudits}
                        selectedDateRange={state.selectedDateRange}
                        onDateRangeOptionChange={(value) => {
                            setState(prev => ({ ...prev, selectedDateRange: value }));
                            if (state.selectedPoint) {
                                fetchSoilData(state.selectedPoint.point_number, value);
                            }
                        }}
                    />
                </Space>
            </Card>

            {state.error ? (
                <Alert message={state.error} type="error" className="mb-8" />
            ) : (
                state.soilData.length > 0 && (
                    <>
                        <Card className="mb-8 shadow-md">
                            <Title level={3} className="mb-4">Latest Received Data</Title>
                            <Text className="mb-4 block">
                                <strong>Reception Date:</strong> {
                                    new Date(state.soilData[state.soilData.length - 1].reception_date).toLocaleString()
                                }
                            </Text>
                            {RenderSensorCards()}
                        </Card>

                        <div className="text-center mb-8">
                            <Button
                                type="primary"
                                className="bg-indigo-600 hover:bg-indigo-700"
                                onClick={() => setState(prev => ({ ...prev, showGraphs: !prev.showGraphs }))}
                            >
                                {state.showGraphs ? 'Hide Graphics' : 'Show Graphics'}
                            </Button>
                        </div>

                        {state.showGraphs && (
                            <SoilMetricsGraphs
                                soilData={state.soilData}
                                supportedSensors={state.supportedSensors}
                                selectedPoint={state.pointInfo}
                                isAdminUser={isAdminUser} // Add this line
                            />
                        )}
                    </>
                )
            )}
        </div>
    );
};

export default SoilInfoPage;